import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { GET_CREATOR_COMPETITIONS } from "MVVM/GraphQL/competitions";
import { ICompetition } from "../../Competitions/@types";
import { getCompetitionPhase } from "../../Competitions/utils";

const POOL_SIZE = 10;

export const CompetitionsBannerViewModel = () => {
  const { data, loading } = useQuery(GET_CREATOR_COMPETITIONS, {
    variables: {
      sort: "ENDING_SOONEST",
      first: POOL_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });

  // Recently-ended competitions are fetched separately so they reliably
  // backfill the "others" row instead of being crowded out of the shared
  // POOL_SIZE limit by active/upcoming ones.
  const { data: endedData } = useQuery(GET_CREATOR_COMPETITIONS, {
    variables: {
      status: "ENDED",
      sort: "ENDING_SOONEST",
      first: POOL_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });

  const { featured, others } = useMemo(() => {
    const all = (data?.creatorCompetitions?.nodes ?? []) as ICompetition[];
    const ended = (endedData?.creatorCompetitions?.nodes ??
      []) as ICompetition[];

    const active = all.filter((c) => getCompetitionPhase(c) === "active");
    const upcoming = all
      .filter((c) => getCompetitionPhase(c) === "upcoming")
      .sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
      );

    const featured =
      active.find((c) => c.sponsored) ??
      upcoming.find((c) => c.sponsored) ??
      null;

    const live = [...active, ...upcoming].filter(
      (c) => c._id !== featured?._id,
    );

    // Most-recently-ended first, so the row stays fresh and drives FOMO.
    const recentlyEnded = ended
      .filter((c) => getCompetitionPhase(c) === "ended")
      .sort(
        (a, b) => new Date(b.endsAt).getTime() - new Date(a.endsAt).getTime(),
      );

    // Live/upcoming take priority; ended competitions backfill the rest.
    // The View trims this to however many cards fill exactly one row, so
    // hand it the full ordered candidate list (no dead space, no wrap).
    const others = [...live, ...recentlyEnded];

    return { featured, others };
  }, [data, endedData]);

  return { featured, others, loading };
};
